import type { Folder } from '@/api';
import { NOT_A_MOVE_TARGET } from '@/features/dnd/messageDrag';

/** A move destination, flattened out of the folder tree for the submenu. */
export interface MoveTarget {
  folder: Folder;
  /** Nesting depth, rendered as indentation. Role folders are always 0. */
  depth: number;
}

/** Role folders come first, in the order a mail user thinks of them. */
const ROLE_ORDER: Folder['role'][] = ['inbox', 'archive', 'junk', 'trash'];

/**
 * Every folder a message can be moved to, flattened for the Move-to submenu
 * (ADR 0019 §2.1): selectable folders only, the message's current folder
 * omitted, role folders first and then the rest in tree order with indentation.
 *
 * `\Noselect` containers are excluded because IMAP cannot store a message in
 * one — offering it would produce a server error the user cannot act on. The
 * job folders (Scheduled, Snoozed) are excluded because ADR 0076's job table
 * drives them: a message filed there by hand is one no job knows about. A drop
 * on the folder tree refuses the same folders (`features/dnd/messageDrag.ts`).
 */
export function flattenMoveTargets(folders: Folder[], currentFolderId: string | undefined): MoveTarget[] {
  const usable = folders.filter(
    (folder) =>
      folder.selectable && !NOT_A_MOVE_TARGET.has(folder.role) && folder.id !== currentFolderId,
  );

  const roles: MoveTarget[] = [];
  for (const role of ROLE_ORDER) {
    const folder = usable.find((candidate) => candidate.role === role);
    if (folder) roles.push({ folder, depth: 0 });
  }
  const taken = new Set(roles.map((target) => target.folder.id));

  const childrenOf = new Map<string | null, Folder[]>();
  for (const folder of usable) {
    const siblings = childrenOf.get(folder.parentId) ?? [];
    siblings.push(folder);
    childrenOf.set(folder.parentId, siblings);
  }
  for (const siblings of childrenOf.values()) {
    siblings.sort((a, b) => a.name.localeCompare(b.name));
  }

  const rest: MoveTarget[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const folder of childrenOf.get(parentId) ?? []) {
      if (!taken.has(folder.id)) rest.push({ folder, depth });
      // A role folder's children keep their place in the tree, one level in.
      walk(folder.id, taken.has(folder.id) ? depth : depth + 1);
    }
  };
  // Folders whose parent is not in the usable set (the current folder's
  // children, say) would otherwise be unreachable, so they are walked as roots.
  const usableIds = new Set(usable.map((folder) => folder.id));
  walk(null, 0);
  for (const folder of usable) {
    if (folder.parentId !== null && !usableIds.has(folder.parentId)) {
      if (!taken.has(folder.id)) rest.push({ folder, depth: 0 });
      walk(folder.id, 1);
    }
  }

  return [...roles, ...rest];
}

/** One mailbox's folders, for the multi-mailbox Move to / Copy to lists. */
export interface MailboxFolders {
  accountId: string;
  /** The group heading: the mailbox's address. */
  label: string;
  folders: Folder[];
}

/** A menu item's worth of target; the caller adds `kind` and `onSelect`. */
export interface MailboxMoveItem {
  id: string;
  label: string;
  depth: number;
  group: string;
  disabled?: boolean;
  hint?: string;
  accountId: string;
  folderId: string;
}

/**
 * Every mailbox's folders, grouped by mailbox (ADR 0108): the Move to and
 * Copy to lists once the owner has more than one mailbox, and the way a phone
 * moves mail between them (no drag there).
 *
 * `sources` are the accounts the selected rows come from. A folder of a
 * mailbox outside it is a transfer for at least one row, and offline every
 * such target is disabled with `offlineReason`: the transfer runs on the
 * server and is never queued. `current` is left out of its own mailbox's
 * list, as `flattenMoveTargets` does for one mailbox.
 */
export function mailboxMoveItems({
  mailboxes,
  sources,
  current,
  online,
  offlineReason,
  idPrefix,
}: {
  mailboxes: readonly MailboxFolders[];
  sources: ReadonlySet<string>;
  current?: { accountId: string; folderId: string | undefined } | undefined;
  online: boolean;
  offlineReason: string;
  idPrefix: string;
}): MailboxMoveItem[] {
  return mailboxes.flatMap((mailbox) => {
    const exclude = current?.accountId === mailbox.accountId ? current.folderId : undefined;
    const crosses = [...sources].some((source) => source !== mailbox.accountId);
    return flattenMoveTargets(mailbox.folders, exclude).map((target) => ({
      id: `${idPrefix}-${mailbox.accountId}-${target.folder.id}`,
      label: target.folder.name,
      depth: target.depth,
      group: mailbox.label,
      accountId: mailbox.accountId,
      folderId: target.folder.id,
      ...(!online && crosses ? { disabled: true, hint: offlineReason } : {}),
    }));
  });
}
